import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, CreditCard, Bell, Check, CheckCheck, Trash2 } from 'lucide-react';
import { useNotifications, type Notification } from '../hooks/useNotifications';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

interface NotificationsPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function NotificationsPanel({ isOpen, onClose }: NotificationsPanelProps) {
    const navigate = useNavigate();
    const {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications,
    } = useNotifications();

    const getNotificationIcon = (type: Notification['type']) => {
        switch (type) {
            case 'order':
                return <ShoppingCart className="h-5 w-5 text-blue-500" />;
            case 'payment':
                return <CreditCard className="h-5 w-5 text-green-500" />;
            case 'system':
                return <Bell className="h-5 w-5 text-gray-500" />;
            default:
                return <Bell className="h-5 w-5 text-gray-500" />;
        }
    };

    const handleNotificationClick = (notification: Notification) => {
        // Marcar como leída si no lo está
        if (!notification.read) {
            markAsRead(notification.id);
        }

        // Navegar según el tipo de notificación
        if (notification.type === 'order') {
            navigate('/orders');
            onClose(); // Cerrar el panel después de navegar
        } else if (notification.type === 'payment') {
            // Puedes agregar navegación para pagos si lo necesitas
            // navigate('/payments');
            // onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/20 z-40"
                    />

                    {/* Panel */}
                    <motion.div
                        initial={{ opacity: 0, x: 300 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 300 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col"
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-4 flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <Bell className="h-6 w-6" />
                                <div>
                                    <h2 className="text-lg font-semibold">Notificaciones</h2>
                                    {unreadCount > 0 && (
                                        <p className="text-sm text-blue-100">
                                            {unreadCount} {unreadCount === 1 ? 'nueva' : 'nuevas'}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Actions */}
                        {notifications.length > 0 && (
                            <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                                {unreadCount > 0 && (
                                    <button
                                        onClick={() => {
                                            markAllAsRead();
                                        }}
                                        className="text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 font-medium flex items-center space-x-1 px-2 py-1 rounded transition-colors"
                                        title="Marcar todas las notificaciones como leídas"
                                    >
                                        <CheckCheck className="h-4 w-4" />
                                        <span>Marcar todas leídas</span>
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        if (confirm('⚠️ ¿Estás seguro?\n\nEsto eliminará PERMANENTEMENTE todas las notificaciones y no se podrán recuperar.')) {
                                            clearAllNotifications();
                                        }
                                    }}
                                    className="text-sm text-red-600 hover:text-red-800 hover:bg-red-50 font-medium flex items-center space-x-1 px-2 py-1 rounded transition-colors ml-auto"
                                    title="Eliminar permanentemente todas las notificaciones"
                                >
                                    <Trash2 className="h-4 w-4" />
                                    <span>Eliminar todas</span>
                                </button>
                            </div>
                        )}

                        {/* Notifications List */}
                        <div className="flex-1 overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center justify-center h-full">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-gray-400 px-4">
                                    <Bell className="h-16 w-16 mb-4 opacity-50" />
                                    <p className="text-center text-gray-500">No tienes notificaciones</p>
                                    <p className="text-sm text-center text-gray-400 mt-2">
                                        Aquí aparecerán tus pedidos y pagos
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {notifications.map((notification) => (
                                        <motion.div
                                            key={notification.id}
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, x: -100 }}
                                            onClick={() => handleNotificationClick(notification)}
                                            className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors relative ${!notification.read ? 'bg-blue-50/50' : ''
                                                }`}
                                        >
                                            {/* Badge de no leída */}
                                            {!notification.read && (
                                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-blue-500 rounded-r" />
                                            )}

                                            <div className="flex items-start space-x-3">
                                                {/* Icon */}
                                                <div className="flex-shrink-0 mt-1">
                                                    {getNotificationIcon(notification.type)}
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm font-semibold ${!notification.read ? 'text-gray-900' : 'text-gray-700'
                                                        }`}>
                                                        {notification.title}
                                                    </p>
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        {notification.message}
                                                    </p>
                                                    <p className="text-xs text-gray-400 mt-2">
                                                        {formatDistanceToNow(new Date(notification.created_at), {
                                                            addSuffix: true,
                                                            locale: es,
                                                        })}
                                                    </p>
                                                </div>

                                                {/* Actions */}
                                                <div className="flex-shrink-0 flex items-center space-x-2">
                                                    {!notification.read && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                markAsRead(notification.id);
                                                            }}
                                                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                                                            title="Marcar como leída"
                                                        >
                                                            <Check className="h-4 w-4 text-gray-500" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            deleteNotification(notification.id);
                                                        }}
                                                        className="p-1 hover:bg-red-100 rounded transition-colors"
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {notifications.length > 0 && (
                            <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
                                <p className="text-xs text-gray-500 text-center">
                                    Mostrando {notifications.length} notificación{notifications.length !== 1 ? 'es' : ''}
                                </p>
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
